import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent } from '../types.js';
import { featuredItem } from './cafe-panels.js';
import { bilingual, hasTitle, type MenuTemplate, type RenderContext } from './common.js';
import {
  IW_FAMILY,
  IW_THEME,
  IW_VARIANTS,
  iwCombo,
  iwComboCss,
  iwDishes,
  iwDishesCss,
  iwDishesHeight,
  iwHeading,
  iwPhoto,
  iwPhotoCss,
  iwRootCss,
  iwScriptCss,
  iwScriptHeight,
} from './ink-wall-panels.js';

/**
 * Ink Wall, layout B: one screen in the Ink Wall look. The board's title in huge brush script
 * over the menu in two columns on one side, and on the other the featured dish as a big photo
 * melting into the black with its red combo disc. Portrait stacks title, photo and lists.
 */

const S = '.mb-t-ink-wall-hero';
const TITLE_EM = 7;
const HEAD_EM = 3.2;
/** Height of the photo in portrait. */
const PORTRAIT_PHOTO = 44;

export function iwBlockHeight(board: MenuBoardContent, perLine: number, width: number, headEm = HEAD_EM): MenuColumn['blockCost'] {
  return (section, items, continued) =>
    (hasTitle(section) ? iwScriptHeight(board, section.title, section.titleAr, headEm, 13) + 0.6 : 0) +
    (!continued && (section.subtitle?.trim() || section.subtitleAr?.trim()) ? 1.8 : 0) +
    iwDishesHeight(board, items, perLine, width) +
    1.8;
}

function titleHeight(board: MenuBoardContent, portrait: boolean): number {
  const brand = board.brand.name || board.brand.nameAr ? 1.3 * 1.4 + 0.3 : 0;
  return brand + iwScriptHeight(board, board.settings.title ?? board.name, board.settings.titleAr, TITLE_EM, portrait ? 14 : 15, 2) + 1.8;
}

function columns(board: MenuBoardContent, portrait: boolean, hero: boolean): MenuColumn[] {
  const capacity = portrait ? 120 - 5 - titleHeight(board, true) - (hero ? PORTRAIT_PHOTO + 2 : 0) : 67.5 - 5 - titleHeight(board, false);
  const column: MenuColumn = { capacity, blockCost: iwBlockHeight(board, hero && !portrait ? 44 : 50, hero && !portrait ? 28 : 34) };
  return [column, column];
}

/** A section: script heading, red note, then its dishes. */
export function iwBlock(ctx: RenderContext, b: MenuBlock, p: string): string {
  const head = hasTitle(b.section) ? iwHeading(ctx, b.section.title, b.section.titleAr, `${p}-st`, 13) : '';
  const sub = b.continued ? '' : bilingual(ctx, b.section.subtitle, b.section.subtitleAr, `${p}-sub`);
  return `<div class="${p}-sec">${head}${sub}${iwDishes(ctx, b)}</div>`;
}

export function iwBlockCss(S: string, p: string): string {
  return `
${iwScriptCss(S, `${p}-st`, HEAD_EM)}
${S} .${p}-sec{margin-bottom:1.8em}
${S} .${p}-sts{margin-bottom:.6em}
${S} .${p}-sub{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.15em;line-height:1.4;letter-spacing:.14em;text-transform:uppercase;
  color:var(--mb-accent);margin:-.3em 0 .7em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${p}-sub.mb-ar{letter-spacing:0}`;
}

/** Brand in spaced red capitals over the board's title in brush script. */
export function iwTitle(ctx: RenderContext, p: string, perLine: number): string {
  const { board } = ctx;
  return [
    `<div class="${p}-head">`,
    bilingual(ctx, board.brand.name, board.brand.nameAr, `${p}-brand`),
    iwHeading(ctx, board.settings.title ?? board.name, board.settings.titleAr, `${p}-title`, perLine),
    '</div>',
  ].join('');
}

export function iwTitleCss(S: string, p: string, em: number): string {
  return `
${S} .${p}-head{min-width:0;margin-bottom:1.8em}
${S} .${p}-brand{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.3em;line-height:1.4;letter-spacing:.3em;text-transform:uppercase;
  color:var(--mb-accent);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${p}-brand.mb-ar{letter-spacing:0}
${S} .${p}-brand.mb-2nd{display:none}
${iwScriptCss(S, `${p}-title`, em)}
${S} .${p}-title:not(.mb-ar){-webkit-line-clamp:2}`;
}

export const inkWallHero: MenuTemplate = {
  id: 'ink-wall-hero',
  name: 'Ink Wall · Hero',
  description:
    'One screen in the Ink Wall look: the title in huge brush script over the menu in two columns, beside the featured dish as a big photo melting into the black with its red combo disc. Matches the Ink Wall panels.',
  defaultTheme: IW_THEME,
  variants: IW_VARIANTS,
  cuisines: ['burgers', 'drinks'],
  family: { ...IW_FAMILY, layout: 'Hero' },
  uses: { featured: true, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const featured = featuredItem(board);
    return packColumns(groupBySection(board, featured?.id), columns(board, orientation === 'portrait', Boolean(featured)), {
      balance: true,
    });
  },

  render(ctx) {
    const item = featuredItem(ctx.board);
    const portrait = ctx.orientation === 'portrait';
    const lists = ctx.page.columns.map((c) => `<div class="iw2-col">${c.map((b) => iwBlock(ctx, b, 'iw2')).join('')}</div>`).join('');
    const hero = item
      ? `<div class="iw2-hero">${iwPhoto(ctx, item.imageMediaId, 'iw2-photo')}${iwCombo(ctx, item, 'iw-combo iw2-combo')}</div>`
      : '';
    return [
      `<div class="iw2-grid${item ? '' : ' iw2-no-hero'}">`,
      iwTitle(ctx, 'iw2', portrait ? 14 : 15),
      hero,
      `<div class="iw2-cols">${lists}</div>`,
      '</div>',
    ].join('');
  },

  css: `
${iwRootCss(S)}
${S}{padding:2.5em 3em}
${iwPhotoCss(S)}
${iwDishesCss(S)}
${iwComboCss(S)}
${iwTitleCss(S, 'iw2', TITLE_EM)}
${iwBlockCss(S, 'iw2')}
${S} .iw2-grid{flex:1;min-height:0;display:grid;grid-template-columns:minmax(0,54fr) minmax(0,46fr);grid-template-rows:auto 1fr;
  grid-template-areas:"head hero" "list hero";column-gap:3em}
${S} .iw2-no-hero{grid-template-columns:minmax(0,1fr);grid-template-areas:"head" "list"}
${S}.mb-portrait .iw2-grid{grid-template-columns:minmax(0,1fr);grid-template-rows:auto auto 1fr;grid-template-areas:"head" "hero" "list"}
${S}.mb-portrait .iw2-no-hero{grid-template-rows:auto 1fr;grid-template-areas:"head" "list"}
${S} .iw2-head{grid-area:head}
${S} .iw2-head .mb-2nd,${S} .iw2-sts .mb-2nd{text-align:left}
${S} .iw2-cols{grid-area:list;min-height:0;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));column-gap:3em}
${S} .iw2-col{min-width:0;display:flex;flex-direction:column}
${S} .iw2-hero{grid-area:hero;position:relative;min-height:0;margin:-2.5em -3em -2.5em 0}
${S}[dir=rtl] .iw2-hero{margin:-2.5em 0 -2.5em -3em}
${S}.mb-portrait .iw2-hero{height:${PORTRAIT_PHOTO}em;margin:0 -3em 2em}
${S} .iw2-photo{position:absolute;inset:0}
${S} .iw2-photo .iw-img{-webkit-mask-image:linear-gradient(90deg,transparent,#000 22%,#000 88%,transparent);mask-image:linear-gradient(90deg,transparent,#000 22%,#000 88%,transparent)}
${S}.mb-portrait .iw2-photo .iw-img{-webkit-mask-image:linear-gradient(transparent,#000 15%,#000 82%,transparent);mask-image:linear-gradient(transparent,#000 15%,#000 82%,transparent)}
${S}[dir=rtl] .iw2-photo .iw-img{-webkit-mask-image:linear-gradient(270deg,transparent,#000 22%,#000 88%,transparent);mask-image:linear-gradient(270deg,transparent,#000 22%,#000 88%,transparent)}
${S} .iw2-combo{position:absolute;z-index:2;left:2em;bottom:4em}
${S}[dir=rtl] .iw2-combo{left:auto;right:2em}
${S}.mb-portrait .iw2-combo{left:auto;right:4em;bottom:1em}
${S}.mb-portrait[dir=rtl] .iw2-combo{right:auto;left:4em}
`,
};
